// src/components/RefundDetail.js
// /refunds/:refundId — one refund: its money (amount, parts, what the ticket
// declared and how much of it was granted), its decision and settlement, who
// recorded it (the AI agent quotes the carrier's own words) and its history.
// The shared RefundForm edits it (PATCH); deleting asks first (soft delete on
// the backend: an agent-recorded refund is not re-created by the agent).

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Banknote, ChevronRight, ExternalLink, FileText, Landmark, Pencil, Save, Scale, Sparkles, Trash2, TriangleAlert, UserRound,
} from 'lucide-react';
import RefundForm from './RefundForm';
import {
  Badge, Card, ConfirmDialog, DescriptionList, EmptyState, Kpi, OverflowMenu, PageHeader, Timeline, Toast,
} from '../design/ui';
import { refundAmountParts, refundFormFromRefund, refundPayload } from '../utils/refunds';
import {
  LIVE_STATUSES, STATUS_TONE, amountState, formatAmount, formatPercent, formatStamp,
} from './refundsMoney';
import './Refunds.css';

const PART_LABEL_KEYS = {
  shipping_cost_refund_amount: ['refunds.shippingAmount', 'Transport'],
  content_refund_amount: ['refunds.contentAmount', 'Contenu'],
  insurance_refund_amount: ['refunds.insuranceAmount', 'Assurance'],
};

const TIMELINE_TONE = { approved: 'accent', processing: 'warning', paid: 'success', rejected: 'danger' };

const RefundDetail = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || 'fr';
  const { refundId } = useParams();
  const navigate = useNavigate();
  const [refund, setRefund] = useState(null);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [toast, setToast] = useState(null);

  const token = localStorage.getItem('access_token');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchRefund = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const response = await axios.get(`/api/logistics/refunds/${refundId}`, { headers });
      setRefund(response.data);
      setForm(refundFormFromRefund(response.data));
    } catch (err) {
      console.error('Failed to fetch refund:', err);
      setLoadError(err?.response?.data?.detail || t('refunds.fetchDetailError', 'Impossible de charger le remboursement.'));
    } finally {
      setLoading(false);
    }
  }, [headers, refundId, t]);

  useEffect(() => {
    fetchRefund();
  }, [fetchRefund]);

  const handleSave = async (event) => {
    event.preventDefault();
    if (!form) return;
    setSaving(true);
    try {
      const response = await axios.patch(`/api/logistics/refunds/${refundId}`, refundPayload(form), { headers });
      setRefund(response.data);
      setForm(refundFormFromRefund(response.data));
      setToast({ tone: 'success', text: t('refunds.updateSuccess', 'Remboursement mis à jour.') });
    } catch (err) {
      console.error('Failed to update refund:', err);
      const detail = err?.response?.data?.detail;
      setToast({ tone: 'danger', text: typeof detail === 'string' ? detail : t('refunds.updateError', 'Échec de la mise à jour du remboursement.') });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`/api/logistics/refunds/${refundId}`, { headers });
      navigate('/refunds');
    } catch (err) {
      console.error('Failed to delete refund:', err);
      const detail = err?.response?.data?.detail;
      setToast({ tone: 'danger', text: typeof detail === 'string' ? detail : t('refunds.deleteError', 'Échec de la suppression du remboursement.') });
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  const breadcrumb = (
    <>
      <Link to="/refunds">{t('refunds.title', 'Remboursements')}</Link>
      <ChevronRight aria-hidden="true" className="refund-detail-v2__crumb-sep" />
    </>
  );

  if (loading) {
    return (
      <div className="u-page u-page--narrow refund-detail-v2" aria-busy="true" aria-label={t('refunds.loadingDetail', 'Chargement du remboursement…')}>
        <div className="refund-detail-v2__header-skeleton">
          <span className="u-skeleton" />
          <span className="u-skeleton" />
        </div>
        <div className="u-well">
          <div className="u-kpi-row">
            {[0, 1, 2, 3].map((i) => <span key={i} className="u-skeleton refunds-v2__kpi-skeleton" />)}
          </div>
          <span className="u-skeleton refunds-v2__card-skeleton" />
        </div>
      </div>
    );
  }

  if (!refund || !form) {
    return (
      <div className="u-page u-page--narrow refund-detail-v2">
        <PageHeader breadcrumb={breadcrumb} title={t('refunds.detailTitle', 'Détail du remboursement')} />
        <div className="u-well">
          <section className="u-card">
            <EmptyState
              icon={TriangleAlert}
              title={loadError || t('refunds.notFound', 'Remboursement introuvable.')}
              action={(
                <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={() => navigate('/refunds')}>
                  {t('refunds.backToRefunds', 'Retour aux remboursements')}
                </button>
              )}
            />
          </section>
        </div>
      </div>
    );
  }

  const currency = refund.currency || 'EUR';
  const money = (amount, code = currency) => formatAmount(amount, code, lang);
  const byAi = refund.created_by_kind === 'ai';
  const client = refund.client_name || refund.merchant_name;
  const reference = refund.external_ticket_id
    || (refund.order_reference && t('refunds.orderShort', 'Cde {{order}}', { order: refund.order_reference }))
    || String(refund._id).slice(-8);
  const subtitle = [
    refund.external_ticket_id && refund.order_reference && t('refunds.orderShort', 'Cde {{order}}', { order: refund.order_reference }),
    client,
    refund.carrier_name,
    t('ordersMoneyUi.detail.createdOn', 'créé le {{date}}', { date: formatStamp(refund.created_at, lang) }),
  ].filter(Boolean).join(' · ');

  const state = amountState(refund);
  const parts = refundAmountParts(refund);
  const history = (Array.isArray(refund.history) ? refund.history : []).slice().reverse();
  const lastChange = history[0];
  const declared = refund.declared || {};
  const declaredTotal = (Number(declared.goods_value) || 0) + (Number(declared.shipping_price) || 0);
  const declaredCurrency = declared.currency || currency;
  const coverage = declaredTotal > 0 && state === 'amount' && declaredCurrency === currency
    ? Number(refund.total_refund_amount) / declaredTotal : null;
  const dirty = JSON.stringify(form) !== JSON.stringify(refundFormFromRefund(refund));
  const who = (kind, email) => (kind === 'ai'
    ? t('refunds.recordedByAiOption', 'Agent IA')
    : (email || t('refunds.recordedByTeam', 'Équipe')));

  return (
    <div className="u-page u-page--narrow refund-detail-v2">
      <PageHeader
        breadcrumb={breadcrumb}
        title={t('ordersMoneyUi.detail.title', 'Remboursement {{reference}}', { reference })}
        subtitle={subtitle}
        actions={(
          <>
            {refund.ticket_id && (
              <button type="button" className="u-btn u-btn--secondary" onClick={() => navigate(`/tickets/${refund.ticket_id}/messages`)}>
                <ExternalLink aria-hidden="true" />
                {t('refunds.openTicket', 'Ouvrir le ticket')}
              </button>
            )}
            <OverflowMenu
              label={t('ordersMoneyUi.moreActions', 'Plus d’actions')}
              items={[{
                label: t('refunds.deleteRefund', 'Supprimer le remboursement'),
                icon: Trash2,
                danger: true,
                onClick: () => setConfirmDelete(true),
              }]}
            />
          </>
        )}
      />

      <div className="u-well">
        <div className="u-kpi-row">
          <div className={state === 'amount' ? 'refund-detail-v2__kpi' : 'refund-detail-v2__text-kpi'}>
          <Kpi
            icon={Banknote}
            tone={state === 'pending' ? 'warning' : state === 'none' ? 'neutral' : 'success'}
            label={t('ordersMoneyUi.detail.kpiAmount', 'Montant')}
            value={state === 'amount' ? money(refund.total_refund_amount)
              : state === 'pending' ? t('ordersMoneyUi.detail.toConfirm', 'À confirmer') : '—'}
            context={state === 'amount'
              ? parts.map((part) => `${t(...PART_LABEL_KEYS[part.key])} ${money(part.value)}`).join(' · ')
              : state === 'pending'
                ? t('ordersMoneyUi.detail.kpiAmountPending', 'accordé, montant pas encore donné')
                : t('ordersMoneyUi.detail.kpiAmountNone', 'aucune somme accordée')}
            contextTone={state === 'pending' ? 'warning' : undefined}
          />
          </div>
          <div className="refund-detail-v2__text-kpi">
          <Kpi
            icon={Scale}
            tone={STATUS_TONE[refund.status] === 'accent' ? undefined : (STATUS_TONE[refund.status] || 'neutral')}
            label={t('refunds.status', 'Statut')}
            value={t(`refunds.statuses.${refund.status}`, refund.status)}
            context={lastChange?.at
              ? t('ordersMoneyUi.detail.since', 'depuis le {{date}} · {{who}}', { date: formatStamp(lastChange.at, lang), who: who(lastChange.by_kind, lastChange.by_email) })
              : undefined}
          />
          </div>
          {declaredTotal > 0 && (
            <Kpi
              icon={FileText}
              tone="neutral"
              label={t('ordersMoneyUi.detail.kpiDeclared', 'Déclaré sur le ticket')}
              value={money(declaredTotal, declaredCurrency)}
              context={coverage !== null
                ? t('ordersMoneyUi.detail.kpiCoverage', 'accordé : {{share}} du déclaré', { share: formatPercent(coverage, lang) })
                : t('ordersMoneyUi.detail.kpiDeclaredContext', 'marchandise + frais de port')}
              meter={coverage !== null ? { value: Math.min(100, Math.round(coverage * 100)), tone: 'success', label: t('ordersMoneyUi.detail.kpiDeclared', 'Déclaré sur le ticket') } : undefined}
            />
          )}
          <div className="refund-detail-v2__text-kpi">
          <Kpi
            icon={Landmark}
            tone="neutral"
            label={t('refunds.settlement', 'Règlement')}
            value={t(`refunds.settlements.${refund.settlement || 'unknown'}`, refund.settlement || '—')}
            context={refund.carrier_reference
              ? t('ordersMoneyUi.detail.carrierFile', 'dossier {{reference}}', { reference: refund.carrier_reference })
              : t('ordersMoneyUi.detail.noCarrierFile', 'réf. de dossier transporteur non saisie')}
          />
          </div>
        </div>

        <div className="refund-detail-v2__grid">
          <form className="u-card" onSubmit={handleSave}>
            <div className="u-card__header">
              <span className="u-icon-chip" aria-hidden="true"><Pencil strokeWidth={1.8} /></span>
              <div className="u-card__heading">
                <h2 className="u-card__title">{t('ordersMoneyUi.detail.formTitle', 'Décision et montants')}</h2>
                <p className="u-card__desc">{t('refunds.workflowSubtitle', 'Mettez à jour la source, le motif, le statut et le montant.')}</p>
              </div>
            </div>
            <div className="u-card__body">
              <RefundForm
                form={form}
                onChange={(field, value) => setForm((prev) => ({ ...prev, [field]: value }))}
                declared={refund.declared}
                ticketOrder={refund.order_reference}
                idPrefix="rfm-detail"
              />
            </div>
            <div className="u-card__footer">
              <span className="u-card__footer-note">
                {refund.updated_at && t('ordersMoneyUi.detail.updated', 'Modifié le {{date}} par {{who}}', {
                  date: formatStamp(refund.updated_at, lang, { time: true }), who: who(refund.updated_by_kind, refund.updated_by_email),
                })}
              </span>
              {dirty && (
                <button type="button" className="u-btn u-btn--secondary" onClick={() => setForm(refundFormFromRefund(refund))} disabled={saving}>
                  {t('ordersMoneyUi.detail.discard', 'Annuler les modifications')}
                </button>
              )}
              <button type="submit" className="u-btn u-btn--primary" disabled={saving || !dirty}>
                <Save aria-hidden="true" />
                {saving ? t('refunds.saving', 'Enregistrement…') : t('refunds.saveChanges', 'Enregistrer')}
              </button>
            </div>
          </form>

          <div className="refund-detail-v2__side">
            <Card
              icon={byAi ? Sparkles : UserRound}
              iconTone={byAi ? undefined : 'neutral'}
              title={t('refunds.provenance', 'Provenance')}
              badge={byAi ? <Badge tone="accent" icon={Sparkles}>{t('refunds.recordedByAi', 'IA')}</Badge> : null}
              description={byAi
                ? t('refunds.provenanceAi', 'Enregistré par l’agent IA depuis la réponse du transporteur')
                : t('refunds.provenanceHuman', 'Enregistré par {{who}}', { who: refund.created_by_email || t('refunds.recordedByTeam', 'Équipe') })}
            >
              {refund.evidence?.text && (
                <figure className="refund-detail-v2__evidence">
                  <figcaption className="u-overline">{t('refunds.evidence', 'Les mots du transporteur')}</figcaption>
                  <blockquote>« {refund.evidence.text} »</blockquote>
                </figure>
              )}
              {history.length > 0 && (
                <div className="refund-detail-v2__history">
                  <span className="u-overline">{t('refunds.history', 'Historique')}</span>
                  <Timeline
                    items={history.map((entry, index) => ({
                      key: `${entry.status}-${entry.at}-${index}`,
                      icon: entry.by_kind === 'ai' ? Sparkles : UserRound,
                      tone: TIMELINE_TONE[entry.status],
                      title: (
                        <Badge tone={STATUS_TONE[entry.status]} dot={LIVE_STATUSES.includes(entry.status)}>
                          {t(`refunds.statuses.${entry.status}`, entry.status)}
                        </Badge>
                      ),
                      meta: who(entry.by_kind, entry.by_email),
                      time: formatStamp(entry.at, lang, { time: true }),
                    }))}
                  />
                </div>
              )}
            </Card>

            <Card icon={FileText} iconTone="neutral" title={t('ordersMoneyUi.detail.references', 'Références')}>
              <DescriptionList
                items={[
                  { label: t('refunds.externalTicketId', 'Ticket externe'), value: refund.external_ticket_id },
                  { label: t('refunds.orderReference', 'Commande'), value: refund.order_reference },
                  { label: t('refunds.clientName', 'Client'), value: client },
                  { label: t('refunds.clientId', 'ID client'), value: refund.client_id },
                  { label: t('refunds.carrierName', 'Transporteur'), value: refund.carrier_ref?.service_label ? `${refund.carrier_name} · ${refund.carrier_ref.service_label}` : refund.carrier_name },
                  { label: t('refunds.trackingNumber', 'Numéro de suivi'), value: refund.tracking_number && <span className="u-code">{refund.tracking_number}</span> },
                  { label: t('refunds.carrierReference', 'Réf. dossier transporteur'), value: refund.carrier_reference },
                  { label: t('refunds.createdAt', 'Créé le'), value: formatStamp(refund.created_at, lang, { time: true }) },
                ]}
              />
            </Card>
          </div>
        </div>
      </div>

      {confirmDelete && (
        <ConfirmDialog
          title={t('ordersMoneyUi.detail.deleteTitle', 'Supprimer le remboursement {{reference}} ?', { reference })}
          message={byAi
            ? t('ordersMoneyUi.detail.deleteMessageAi', 'Il disparaît de la liste et des totaux. L’agent IA est prévenu qu’il a été supprimé et ne le recréera pas depuis la réponse du transporteur.')
            : t('ordersMoneyUi.detail.deleteMessage', 'Il disparaît de la liste des remboursements et des totaux.')}
          confirmLabel={deleting ? t('ordersMoneyUi.deleting', 'Suppression…') : t('ordersMoneyUi.delete', 'Supprimer')}
          cancelLabel={t('refunds.cancel', 'Annuler')}
          onConfirm={handleDelete}
          onCancel={() => setConfirmDelete(false)}
          busy={deleting}
        />
      )}

      {toast && (
        <Toast tone={toast.tone} onClose={() => setToast(null)} closeLabel={t('common.close', 'Fermer')}>
          {toast.text}
        </Toast>
      )}
    </div>
  );
};

export default RefundDetail;
